<template>
  <div class="top-bar__parent top-bar__beci">
    <div
      class="top-bar is-simplified" style="display: flex;">
      <img src="/images/static-home-page/brussels/beci-brussels-logo-text-white.png" alt="Beci Logo Text" style="height: 50px; margin-left: 10px; cursor: pointer;"
           @click="navigateToHome"/>
    </div>

    <div class="top-bar__search tw-container full-width w-full" v-if="!ui.isMobile">
      <GlobalSearch
        ref="globalSearch"
        :placeholder="$t('global_search_placeholder', { ecosystem: ecosystemDisplayName })"
      />
    </div>

    <div
      class="top-bar__help" :class="{ 'top-bar__user--active': dropdown === 'help' }"
      id="introjs-after-onboarding-faq" v-if="!ui.isMobile"
    >
      <ds-button
        block
        @click="toggleDropdown('help')"
        icon="information"
        v-tooltip.top-center="$t('tooltip_support_topbar')"
      />
      <div class="top-bar__user__dropdown">
        <ds-button
          block
          :href="faqUrl"
          @click="trackHeapEvent('topbar.faq')"
          target="_blank"
          :label="$t('help_dropdown_faq')"
          variant="link"
        />
        <!-- hide feedback button for now -->
        <ds-button
          block
          @click="openFeedbackWindow(locale)"
          variant="link"
          :label="$t('help_dropdown_feedback')"
        />
        <ds-button
          block v-if="introJsType" @click="startIntro" label="Show tutorial"
          variant="link"
        />
      </div>
    </div>
    <div
      class="top-bar__user" :class="{'top-bar__user--active': dropdown === 'language'}"
      @click="toggleDropdown('language')"
      v-if="isSimplifiedTemplateUsed && enabledLocales.length > 1 && !ui.isMobile"
      v-tooltip.top-center="$t('generic_languages')"
    >
      <div class="top-bar__user__name">
        {{ $store.state.localization.activeLocale.toUpperCase() }}
      </div>
      <icon name="chevron-down"/>
      <div class="top-bar__user__dropdown" @click.stop>
        <template v-if="isSimplifiedTemplateUsed">
          <ds-button
            v-for="locale in enabledLocales"
            :key="locale"
            block
            @click="setLanguage(locale)"
            :label="LOCALE_LABELS[locale]"
            variant="link"
          />
        </template>
        <ds-button
          block
          @click="setLanguage('cimode')"
          :label="LOCALE_LABELS['cimode']"
          variant="link"
          v-if="showCiMode"
        />
      </div>
    </div>

    <div class="top-bar__user top-bar__user--actions" style="margin-right: 10px;" v-if="!isLoggedIn">
      <ds-button label="Login" variant="primary" @click.prevent="goToLoginPage"/>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import GlobalSearch from '@/components/TopBar/GlobalSearch/GlobalSearch.vue'
  import PortfolioDropdown from '@/components/Dropdown/PortfolioDropdown.vue'
  import TopBarPortfolioKeyword from '@/components/Keyword/TopBarPortfolioKeyword.vue'
  import Avatar from '@/components/Avatar/Avatar.vue'
  import { SiteTemplate } from '@/store/modules/config'
  import { LOCALE_LABELS } from '@/store/modules/localization'
  import moment from '~/moment'
  import { DEFAULT_URLS } from '@/constants/default-urls'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { MUTATION_TYPES as USER_MUTATION_TYPES } from '@/store/modules/user-helpers'
  import { trackHeapEvent } from '@/util/analytics'
  import { ACTION_TYPES as ACTORS_ACTION_TYPES } from '@/store/modules/actors'
  import { ACTION_TYPES as CHALLENGES_ACTION_TYPES } from '@/store/modules/challenges'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import { ACTION_TYPES as NOTIFICATIONS_ACTION_TYPES } from '@/store/modules/notifications'
  import { readNotifications } from '@/api/notifications'
  import { notificationColor, notificationColorClass, notificationIconName, NotificationsMixin } from '@/util/NotificationsMixin'
  import { ACTION_TYPES as USER_ACTION_TYPES } from '@/store/modules/user'
  import MODAL_IDS from '@/constants/modal-ids'
  import { fetchAllFavourites, Portfolios } from '@/api/portfolios'
  import { ConversationsMixin } from '@/util/ConversationsMixin'
  import UiMixin from '@/util/UiMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import IntroJsMixin from '@/util/IntroJsMixin'
  import CommunityMixin from '@/util/CommunityMixin'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    name: "TopBarBeciBrusselsVisitorsOnly",
    components: {
      Avatar,
      TopBarPortfolioKeyword,
      PortfolioDropdown,
      GlobalSearch
    },
    mixins: [NotificationsMixin, ConversationsMixin, UiMixin, TranslationsMixin, IntroJsMixin, CommunityMixin, UserMixin],
    computed: {
      chatbotLabel() {
        if (this.$store.getters.chatbotCustomization) {
          return this.$store.getters.chatbotCustomization.chatbotCTA
        }

        return this.$t('global_search_add_a_question')
      },
      canDisplayChatbot() {
        return !this.ui.isMobile && this.$store.getters.isQandaChatBotEnabled && this.$store.getters.isLoggedIn
      },
      isTeamMember() {
        return this.$store.getters.isTeamMember
      },
      isFoodleap() {
        return this.$store.getters.isFoodleap
      },
      isCommunityEcosystem() {
        return this.$store.getters.isCommunityEcosystem
      },
      canAccessConfiguration() {
        if (this.isCommunityManagerAndPortfolioManager) {
          return true
        }

        if (!this.isMember) {
          return false
        }

        return !this.ui.isMobile || !this.isExplorationOnly
      },
      configurationPageUrl() {
        if (this.ui?.isMobile) {
          return '/datalab/curation'
        }

        if (this.$store.getters.isStakeholderMappingOnly) {
          return '/datalab/spreadsheet'
        }

        if (this.isOwner) {
          return '/settings/ecosystem'
        }

        if (this.isMember) {
          return '/settings/users'
        }

        return '/datalab/curation'
      },
      communitiesOfWhichUserIsMember() {
        if (this.canShowMyCommunities) {
          return this.joinedCommunities
        }
        return []
      },
      canShowMyCommunities() {
        return this.$store.getters.isLoggedIn && !this.$store.getters.isOwner && (this.$store.getters.isActor || this.$store.getters.isMember) && this.hasAccessToNewCommunities
      },
      hasAccessToNewCommunities() {
        return this.$store.getters.hasAccessToCommunities
      },
      isPersonalHomeAvailable() {
        return !this.isPersonalHomeDisabled
      },
      isPersonalHomeDisabled() {
        return !!this.$store.state.config.personalHomeDisabled
      },
      isSimplifiedTemplateUsed() {
        return [SiteTemplate.NEW_SIMPLIFIED, SiteTemplate.SIMPLIFIED].includes(this.$store.getters.activeSiteTemplate)
      },
      isExplorationOnly() {
        return this.$store.getters.isExplorationOnly
      },
      intelligenceCtaUpgradeText() {
        if (this.showIntelligenceUpgradeCTA) {
          return this.config.packageCTAs.intelligence.text || this.$t('default_intelligence_upgrade_cta')
        }
        return ''
      },
      showIntelligenceUpgradeCTA() {
        return this.isLoggedIn && !this.displayExploreContent && this.config.packageCTAs && this.config.packageCTAs.intelligence && this.config.packageCTAs.intelligence.enabled
      },
      canShowEcosystemPackages() {
        return (this.$store.getters.hasStripeKeys || this.$store.getters.isDeveloper) &&
          this.$store.getters.hasAccessToEcosystemMemberPackages
      },
      canShowUserBilling() {
        return this.canShowEcosystemPackages && this.$store.getters.isLoggedIn && this.$store.getters.isActor
      },
      totalNewActorsCount() {
        return this.$store.state.actors.newActorCount
      },
      totalNewNotificationsCount() {
        return this.$store.state.notifications.newNotificationsCount
      },
      totalNewChallengesCount() {
        return this.$store.state.challenges.newChallengesCount
      },
      totalNewProductsCount() {
        return this.$store.state.actors.newProductCount
      },
      isOwner() {
        return this.$store.getters.isOwner
      },
      displayExploreContent() {
        const hasAccess = (this.$store.getters.hasAccessToExploration || this.$store.getters.hasAccessToMonitoring)

        return this.$store.getters.isLoggedIn && hasAccess && this.config.viewDashboard && this.config.viewDashboard.includes('explore-content')
      },
      displayAnnouncements() {
        return this.$store.getters.canUsePublicAnnouncements && this.$store.getters.areMessageBoardsEnabled
      },
      isNewSimplified() {
        return this.config && this.config.siteTemplate === SiteTemplate.NEW_SIMPLIFIED
      },
      viewActorTypes() {
        return this.$store.getters.viewActorTypes
      },
      displayChallenges() {
        return this.$store.state.config.challengesAreEnabled && this.shouldSeeChallenges
      },
      displayKnowledgeBase() {
        return this.$store.getters.hasAccessToKnowledgeBase && this.config.viewDashboard && this.config.viewDashboard.includes('knowledge-base')
      },
      shouldSeeChallenges() {
        if (this.$store.getters.isMember) {
          return true
        }

        return this.$store.getters.canCreateChallenge ||
          this.$store.getters.viewableChallengeStatusses.length > 0 ||
          this.$store.getters.accessibleChallengeStatusses.length > 0
      },
      enabledLocales() {
        return this.$store.state.localization.enabledLocales
      },
      showCiMode() {
        return import.meta.env.APP_ENV === 'local'
      },
      LOCALE_LABELS() {
        return LOCALE_LABELS
      },
      isLoggedIn() {
        return this.$store.getters.isLoggedIn
      },
      isActor() {
        return this.$store.getters.isActor
      },
      isGlobalSearchVisible() {
        return this.$store.state.ui.globalSearch.isVisible
      },
      enableNotifications() {
        return this.isLoggedIn && this.notifications
      },
      unreadConversationsCount() {
        return this.$store.state.conversations.listData.unreadCount
      },
      conversations() {
        return this.$store.state.conversations.listData.data
      },
      unreadNotificationsBadgeCount() {
        return this.unreadNotificationsCount
      },
      sortedUnreadNotifications() {
        return this.unreadNotifications.sort((a, b) => {
          const aMoment = moment(a.created_at)
          const bMoment = moment(b.created_at)

          if (!aMoment.isValid() && !bMoment.isValid()) {
            return 0
          }

          if (!aMoment.isValid()) {
            return 1
          }

          if (!bMoment.isValid()) {
            return -1
          }

          return bMoment.diff(aMoment)
        })
      },
      config() {
        return this.$store.state.config
      },
      profile() {
        return this.$store.state.user.profile
      },
      currentEcosystem() {
        return this.$store.getters.currentEcosystem
      },
      name() {
        return this.currentEcosystem.displayName || this.currentEcosystem.name
      },
      canSwitchEcosystems() {
        return (this.$store.state.user.profile.teams || []).length > 1
      },
      isEcosystemSwitcherVisible() {
        return this.$store.state.ui.ecosystemSwitcher.isVisible
      },
      userHasPortfolioAccess() {
        return this.isLoggedIn && (this.$store.getters.isAdmin || this.$store.getters.isOwner || this.$store.getters.isMember)
      },
      canShowPortfolioDropdownItems() {
        return !(this.$route.path === '/dashboards/home' ||
          this.$route.path === '/portfolios' ||
          this.$route.path === '/actors/create' ||
          this.$route.path === '/dashboards/compare' ||
          this.$route.path === '/dashboards/charts' ||
          this.$route.path === '/dashboards/knowledge-base' ||
          this.$route.path === '/spotting-areas' ||
          this.$route.path.includes('/spotting-areas/') ||
          (this.$route.name === 'datalab' && this.$route.path !== '/datalab/spreadsheet' && this.$route.path !== '/datalab/suggestions') ||
          this.$route.name === 'settings' ||
          (this.$route.path === '/actors' && (this.$route.query.actor_type === 'Community' || this.$route.query.actor_type === 'Person'))
        ) && this.userHasPortfolioAccess
      },
      canShowTelex() {
        // Deprecated
        return false
      },
      personalHomepageEnabled() {
        // Hard coded until we bring back the personalisation on the home page
        return false
        // return this.$store.getters.userSettings && this.$store.getters.userSettings.personalHomepageEnabled
      },
      introJsType() {
        const config = this.buildIntroJsConfig(this.config, this.$store.getters.userRole, true)
        if (this.$route.path === '/dashboards/home' && ![SiteTemplate.SIMPLIFIED, SiteTemplate.NEW_SIMPLIFIED].includes(this.$store.getters.activeSiteTemplate) && config.home && config.home.steps) {
          return 'home'
        }
        if (this.$route.path.startsWith('/settings/ecosystem') && config.settings && config.settings.steps) {
          return 'settings'
        }
        if (this.$route.path.startsWith('/dashboards/concept-map/') && config.conceptMap && config.conceptMap.steps) {
          return 'conceptMap'
        }
        if (this.$route.path.startsWith('/dashboards/explore-content') && config.exploreContent && config.exploreContent.steps) {
          return 'exploreContent'
        }
        if (this.$route.path.startsWith('/profile/update') && (!this.$route.hash || this.$route.hash === '#personal-home') && config.personalHome && config.personalHome.steps) {
          return 'personalHome'
        }
        // if (this.$route.path.startsWith('/actors') && config.simplifiedActorDetail && config.simplifiedActorDetail.steps && this.$route.name === 'actor-detail' && [SiteTemplate.SIMPLIFIED, SiteTemplate.NEW_SIMPLIFIED].includes(this.$store.state.config.siteTemplate)) {
        //   return 'simplifiedActorDetail'
        // }
        // if (this.$route.path.startsWith('/dashboards/home' && [SiteTemplate.SIMPLIFIED].includes(this.$store.getters.activeSiteTemplate) && config.homeCommunities && config.homeCommunities.steps)) {
        //   return 'homeCommunities'
        // }
        // if (this.$route.name === 'communities' && config.communitiesOverviewPage && config.communitiesOverviewPage.steps) {
        //   return 'communitiesOverviewPage'
        // }
        // if (this.$route.name === 'community-detail' && config.communitiesDetailPage && config.communitiesDetailPage.steps) {
        //   return 'communitiesDetailPage'
        // }
        // if (this.$route.name === 'spotting-areas' && config.spottingAreasHome && config.spottingAreasHome.steps) {
        //   return 'spottingAreasHome'
        // }
        // if (this.$route.name === 'spotting-area-detail' && config.spottingAreasDetailPage && config.spottingAreasDetailPage.steps) {
        //   return 'spottingAreasDetailPage'
        // }
        // if (this.$route.path.startsWith('/announcements-simplified') && config.messageBoard && config.messageBoard.steps) {
        //   return 'messageBoard'
        // }
        if (config.personalHome) {
          // If there's a configuration for personalHome and the user is not on one of the pages that has a tutorial, redirect to personal home and start it
          return 'personalHomeRedirect'
        }
      },
      canShowCounters() {
        const isEcosystemMember = this.$store.getters.isLoggedIn && this.$store.getters.userRole === 'actor'
        const canSeeSimplified = this.$store.getters.isPublic || isEcosystemMember
        return canSeeSimplified && this.isNewSimplified
      },
      faqUrl() {
        if (this.isOwner) {
          return DEFAULT_URLS.FAQ
        }
        if (this.config.faqUrl) {
          return 'https://' + this.config.faqUrl
        } else {
          return DEFAULT_URLS.FAQ
        }
      },
      isMember() {
        return this.$store.getters.isMember
      },
      isPortfolioMember() {
        return this.$store.getters.isPortfolioMember
      },
    },
    methods: {
      goToLoginPage() {
        this.$router.push(`/login?redirect_uri=${this.$route.path}`)
      },
      showSidePanel() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, { component: 'chat-bot-side-panel' })
      },
      showFirstConversation() {
        let firstConversationId
        if (this.conversations && this.conversations[0] && this.conversations[0].id) {
          firstConversationId = this.conversations[0].id
        }

        // Note: undefined param when conversations are not fully loaded in
        this.$router.push({
          name: 'profile',
          params: {
            panel: 'conversations',
            id: firstConversationId,
          },
        })
      },
      startIntro() {
        const config = this.buildIntroJsConfig(this.config, this.$store.getters.userRole, true)

        if (['personalHome', 'personalHomeRedirect'].includes(this.introJsType)) {
          // clear all intros to allow re-entering the entire flow
          const seenIntrosWithoutCommunityRelatedIntros = this.seenIntros.filter(intro => !['personalHome', 'simplifiedActorDetail', 'spottingAreasHome', 'spottingAreasDetailPage', 'messageBoard', 'homeCommunities', 'communitiesOverviewPage', 'communitiesDetailPage'].includes(intro))
          this.$store.commit(USER_MUTATION_TYPES.STORE_SETTINGS, { seenIntros: seenIntrosWithoutCommunityRelatedIntros.join(',') })
        }

        /*if (this.introJsType === 'personalHomeRedirect' && !['/profile/update#personal-home', '/profile/update'].includes(this.$route.fullPath)) {
          this.$router.push('/profile/update#personal-home')
          return
        }*/

        const intro = this.introJs()
        this.currentIntro = intro
        config[this.introJsType].steps = this.getIntroJsStepsConfig(config[this.introJsType].steps)

        intro.setOptions(config[this.introJsType])

        const componentScope = this
        intro.onbeforechange(this.introJsBeforeStepCallback.bind(this))
        intro.onafterchange(this.introJsAfterStepCallback.bind(this))
        intro.oncomplete(function () {
          componentScope.introJsOnCompleteCallback(this._options, this._currentStep, componentScope)
        })
        intro.onexit(function () {
          componentScope.introJsOnCompleteCallback(this._options, this._currentStep, componentScope)
        })

        this.$bus.emit('start-intro-' + this.introJsType)
        this.$nextTick(() => intro.start())
      },
      trackHeapEvent,
      fetchActorsCount() {
        this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_NEW_ACTORS_COUNT)
        this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_NEW_PRODUCTS_COUNT)
      },
      fetchChallengesCount() {
        if (this.$store.state.config.challengesAreEnabled) {
          this.$store.dispatch(CHALLENGES_ACTION_TYPES.FETCH_NEW_CHALLENGES_COUNT)
        }
      },
      fetchConversations() {
        if (!this.hasAccessToConversations) {
          return false
        }

        if (!this.$store.state.conversations.listData.loading) {
          setTimeout(() => {
            // The initial load of the app is very API intensive, fetching the conversations has a low priority
            this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          }, 10000)
        }
      },
      fetchNotificationsCount() {
        this.$store.dispatch(NOTIFICATIONS_ACTION_TYPES.FETCH_NEW_NOTIFICATIONS_COUNT)
      },
      openFeedbackWindow() {
        document.querySelector('.userback-button-e').click()
      },
      handleToggleMobileNavigationDrawerVisibility() {
        this.$emit('toggleMobileNavigationDrawerVisibility')
        this.$store.commit(UI_MUTATION_TYPES.HIDE_GLOBAL_SEARCH)
      },
      handleToggleMobileSearchDrawerVisibility() {
        this.$bus.emit('toggleMobileSearchDrawerVisibility')
      },
      // Redirect the user to the notification and open the link in a different tab, also mark the notification as read
      redirectNotification(notification) {
        // Mark the notification as read
        readNotifications([notification.id])

        if (notification.action_url && notification.action_url.startsWith('http')) {
          window.location.href = notification.action_url
        } else {
          window.location.href = '/notifications/' + notification.id
        }
      },
      notificationColor,
      notificationColorClass,
      notificationIconName,
      notificationLink(notification) {
        if (notification.action_url && notification.action_url.startsWith('http')) {
          return notification.action_url
        } else {
          return '/notifications/' + notification.id
        }
      },
      togglePersonalHomepage(state) {
        trackHeapEvent('global.personalHomepageEnabled', state)
        this.$store.commit('USER/STORE_SETTINGS', { personalHomepageEnabled: state })
      },
      setLanguage(locale) {
        this.$store.dispatch('LOCALIZATION/UPDATE_ACTIVE_LOCALE', { locale, i18n: this.$i18n })
      },
      logout() {
        this.$store.dispatch(USER_ACTION_TYPES.LOGOUT_REQUEST)
      },
      handleSelectActor(actor) {
        trackHeapEvent('global.search.clickSearchedActor', {
          id: actor.id,
          medium: 'desktop',
        })

        if (this.$store.getters.views.includes('actors/detail')) {
          // Be sure to fetch it, because ActorDetail may not.
          this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_ACTOR_DETAIL, actor.id)
          this.$router.push(`/actors/${actor.id}`)
        } else if (this.$store.getters.isMember) {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
            component: 'scores',
            metaData: actor.id,
          })
        } else {
          this.$router.push(`/actors`)
          /* this.$store.dispatch('ACTORS/FETCH_ACTOR_FOR_PREVIEW', actor)
            .then(actor => {
              try {
                this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {component:"scores", metaData: actor.id})
                this.$root.$children[0].$refs.view.$refs.mapPane.forceVisibleMarker(actor)
              } catch (e) {}
            }) */
          try {
            this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
              component: 'scores',
              metaData: actor.id,
            })
            this.$root.$children[0].$refs.view.$refs.mapPane.forceVisibleMarker(actor)
          } catch (e) {
          }
          // this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {component:"scores", metaData: actor.id})
        }
      },
      navigateToHome() {
        this.$router.push('/')
      },
      toggleEcosystemSwitcher() {
        if (document.body.clientWidth < 768) {
          this.$router.push('/')
          return
        }

        if (this.canSwitchEcosystems) {
          this.$store.commit(UI_MUTATION_TYPES.TOGGLE_ECOSYSTEM_PANEL)
        } else {
          this.$router.push('/')
        }
      },
      updateSelection(value) {
        this.selection = value
      },
      openSidePanel() {
        if (this.$store.state.ui.sidePanel.isVisible) {
          this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
        } else {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, { component: 'telex' })
        }
      },
      showConfirmationModal() {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: 'Personal Homepage',
          body: 'You don\'t have favourite Portfolios. Better information will be provided by having a favourite portfolio',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },
      fetchFavouritePortfolios() {
        // Fetch all portfolios
        fetchAllFavourites().then(favouritePortfolios => {
          this.hasFavouritePortfolios = favouritePortfolios.length > 0
        }).catch(errors => {
          console.log(errors)
        })
      },
      fetch() {
        // Fetch all portfolios
        Portfolios.get()
          .then(portfolios => {
            this.portfolioList = portfolios
          })
          .catch(errors => {
            this.errors = errors
          })
      },
    },
  })
</script>

<style scoped lang="scss">
  @import "../../../scss/variables";

  .top-bar__beci {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #001489;
  }
</style>
